import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import {Template} from "../../../components/template/template.jsx";
import {useEffect, useRef, useState} from "react";
import {subWeeks} from "date-fns";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import ReportUtils from "../../../utils/report-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import Filters from "./components/filters.jsx";
import Content from "./components/content.jsx";
import NatureActionsService from "../../../services/reports/nature-actions-service.js";

const initialFilter = () => ({
    dtStart: subWeeks(new Date(), 1),
    dtEnd: new Date(),
    dispatch: { value: "all", label: "Todos" },
    nature: [],
    natureGroups: [],
});

const filtersLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    dispatch: "Despacho",
    nature: "Naturezas",
    natureGroups: "Grupo de naturezas",
};

export default function NatureActions() {
    const [isLoading, setIsLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [filters, setFilters] = useState(initialFilter);
    const [reportData, setReportData] = useState(null);

    const [tableColumns, setTableColumns] = useState(() => ({
        nature: {label: "Natureza", visible: true},
        group: {label: "Grupo", visible: true},
        attendanceQuantity: {label: "Atend. (Qtd)", visible: true},
        attendancePercent: {label: "Atend. (%)", visible: true},
        dispatchQuantity: {label: "Desp. (Qtd)", visible: true},
        dispatchPercent: {label: "Desp. (%)", visible: true},
        boQuantity: {label: "BO (Qtd)", visible: true},
        boPercent: {label: "BO (%)", visible: true}
    }));

    const [boName, setBoName] = useState("");
    const [headerSelected, setHeaderSelected] = useState(-1);
    const title = "Ações por naturezas";
    const printComponentRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
            }, filtersLabels),
        );
    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading: setIsLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, null, null);

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setIsLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                dispatch: filters.dispatch?.value ?? "all",
                nature: filters.nature?.map(i => i.value) ?? [],
                nature_group: filters.natureGroups?.map(i => i.value) ?? [],
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
            }, filtersLabels, true);

            const response = await NatureActionsService.apply({ ...formattedFilters, filters: auditFilters });
            setReportData(response.data);
            setBoName(response.boName);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setIsLoading(false);
        }
    };

    const clearSearch = () => {
        setFilters(initialFilter());
        setReportData(null);
    };

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }
        setCollapsed(prev => !prev);
    };

    useGlobalKey("Enter", apply);

    return (
        <SubScroll>
            <div className="flex">
                <Template isLoading={isLoading}>
                    <Template.Header>
                        <Template.Title>{title}</Template.Title>
                        <HeaderButtons
                            exportable={true}
                            exportInSheets={true}
                            printable={true}
                            headerChange={true}
                            visibilityColumns={true}
                            columns={tableColumns}
                            setColumns={setTableColumns}
                            printComponentRef={printComponentRef}
                            tableColumns={tableColumns}
                            reportData={reportData}
                            data={reportData?.length}
                            headerSelected={headerSelected}
                            setHeaderSelected={setHeaderSelected}
                        />
                    </Template.Header>
                    <Template.Body className={`${collapsed ? "p-0!" : ""}`}>
                        <Menu
                            buttonRef={buttonRef}
                            fixedTop={fixedTop}
                            collapsed={collapsed}
                            toggleFilters={toggleFilters}
                            apply={apply}
                            clearSearch={clearSearch}
                        >
                            <Filters filters={filters} setFilters={setFilters} />
                            <AutoDatetime setFilters={setFilters} />
                        </Menu>
                    </Template.Body>
                </Template>
                <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? "mt-[59px]" : "mt-[61px]"} print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? "!bg-[#f3f3f4] !border-none !-translate-y-[5px]" : ""} ${collapsed ? "ml-2" : ""}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || reportData?.length === 0 ? "p-[0]" : "px-[20px] py-[5px]"}`}>
                            <NotAppliedContent reportData={reportData} />
                            <NoDataContent reportData={reportData} data={reportData?.length} />
                            <Content
                                reportData={reportData}
                                boName={boName}
                                data={reportData?.length}
                                entity={headerSelected}
                                title={title}
                                setIsLoading={setIsLoading}
                                printComponentRef={printComponentRef}
                                tableColumns={tableColumns}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </div>
        </SubScroll>
    )
}